import React, { useState, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import {
  Download,
  RefreshCw,
  Mail,
  Calendar,
  Users,
  Lock,
  Handshake
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SignupTable = ({ rows, emptyText, rowTestId, emailTestId, iconColor }) => (
  rows.length === 0 ? (
    <div className="text-center py-12">
      <Mail className="w-12 h-12 mx-auto text-gray-400 mb-4" />
      <p className="text-gray-600 text-lg">{emptyText}</p>
    </div>
  ) : (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b-2 border-purple-200">
            <th className="text-left py-3 px-4 font-semibold text-gray-900">#</th>
            <th className="text-left py-3 px-4 font-semibold text-gray-900">Email</th>
            <th className="text-left py-3 px-4 font-semibold text-gray-900">Signup Date</th>
            <th className="text-left py-3 px-4 font-semibold text-gray-900">IP Address</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((signup, index) => (
            <tr key={signup.email} className="border-b border-gray-200 hover:bg-purple-50 transition-colors" data-testid={rowTestId}>
              <td className="py-3 px-4 text-gray-700">{index + 1}</td>
              <td className="py-3 px-4">
                <div className="flex items-center gap-2">
                  <Mail className={`w-4 h-4 ${iconColor}`} />
                  <span className="font-medium text-gray-900" data-testid={emailTestId}>{signup.email}</span>
                </div>
              </td>
              <td className="py-3 px-4 text-gray-700">
                {new Date(signup.signup_date).toLocaleString()}
              </td>
              <td className="py-3 px-4 text-gray-600 text-sm">
                {signup.ip_address || 'N/A'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
);

const AdminDashboard = () => {
  const { toast } = useToast();
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(false);

  const clients = useMemo(() => signups.filter((s) => (s.source || 'waitlist') !== 'partner'), [signups]);
  const partners = useMemo(() => signups.filter((s) => s.source === 'partner'), [signups]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password) {
      setIsAuthenticated(true);
      fetchSignups();
    }
  };

  const fetchSignups = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/waitlist`, {
        headers: {
          'Authorization': `Bearer ${password}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSignups(data.signups);
      } else {
        toast({
          title: "Authentication Failed",
          description: "Invalid password. Please try again.",
          variant: "destructive"
        });
        setIsAuthenticated(false);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch waitlist data.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (sourceFilter, label) => {
    try {
      const url = sourceFilter
        ? `${BACKEND_URL}/api/admin/waitlist/export?source=${sourceFilter}`
        : `${BACKEND_URL}/api/admin/waitlist/export`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${password}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const objectUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = `unwp_${label}_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();

        toast({
          title: "Export Successful",
          description: `${label === 'partners' ? 'Partner programme' : label === 'waitlist' ? 'Waitlist clients' : 'All signups'} exported to CSV.`,
        });
      } else {
        toast({
          title: "Export Failed",
          description: "Failed to export data.",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to export data.",
        variant: "destructive"
      });
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-violet-50 to-fuchsia-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-4 border-purple-300 shadow-2xl">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-purple-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">Admin Login</CardTitle>
            <CardDescription>Enter admin password to access dashboard</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Input
                  type="password"
                  placeholder="Admin Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 border-2 border-purple-300 focus:border-purple-600"
                  data-testid="admin-password-input"
                />
              </div>
              <Button 
                type="submit"
                className="w-full h-12 bg-gradient-to-r from-purple-600 to-purple-800 hover:from-purple-700 hover:to-purple-900 text-white font-semibold"
                data-testid="admin-login-button"
              >
                Login
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-violet-50 to-fuchsia-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                UNWP Waitlist Dashboard
              </h1>
              <p className="text-gray-600">Manage your early access signups and partners</p>
            </div>
            <Button
              onClick={() => {
                setIsAuthenticated(false);
                setPassword('');
              }}
              variant="outline"
              className="border-2 border-purple-300"
              data-testid="admin-logout-button"
            >
              Logout
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="border-2 border-yellow-300 bg-white shadow-lg">
            <CardHeader className="pb-3">
              <CardDescription>Waitlist Clients</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-purple-800 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-br from-purple-600 to-purple-800" data-testid="stat-clients-count">
                  {clients.length}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-amber-300 bg-white shadow-lg">
            <CardHeader className="pb-3">
              <CardDescription>Partner Programme</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center">
                  <Handshake className="w-6 h-6 text-white" />
                </div>
                <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-br from-amber-500 to-amber-700" data-testid="stat-partners-count">
                  {partners.length}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-yellow-300 bg-white shadow-lg">
            <CardHeader className="pb-3">
              <CardDescription>Progress to Goal</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-purple-900" />
                </div>
                <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-br from-purple-600 to-purple-800">
                  {Math.round((clients.length / 500) * 100)}%
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-yellow-300 bg-white shadow-lg">
            <CardHeader className="pb-3">
              <CardDescription>Spots Remaining</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center">
                  <Mail className="w-6 h-6 text-purple-900" />
                </div>
                <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-br from-purple-600 to-purple-800">
                  {Math.max(500 - clients.length, 0)}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-4 mb-6">
          <Button
            onClick={fetchSignups}
            disabled={loading}
            className="bg-gradient-to-r from-purple-600 to-purple-800 hover:from-purple-700 hover:to-purple-900 text-white"
            data-testid="admin-refresh-button"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={() => handleExport(null, 'all')}
            variant="outline"
            className="border-2 border-purple-300"
            data-testid="admin-export-button"
          >
            <Download className="w-4 h-4 mr-2" />
            Export All (CSV)
          </Button>
        </div>

        {/* Waitlist Clients Table */}
        <Card className="border-2 border-yellow-300 bg-white shadow-lg mb-8">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle>Waitlist Clients</CardTitle>
              <CardDescription>People waiting for early access, newest first</CardDescription>
            </div>
            <Button
              onClick={() => handleExport('waitlist', 'waitlist')}
              variant="outline"
              size="sm"
              className="border-2 border-purple-300"
              data-testid="admin-export-waitlist-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Clients
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-purple-600 mb-2" />
                <p className="text-gray-600">Loading signups...</p>
              </div>
            ) : (
              <SignupTable
                rows={clients}
                emptyText="No waitlist signups yet"
                rowTestId="admin-signup-row"
                emailTestId="admin-signup-email"
                iconColor="text-purple-600"
              />
            )}
          </CardContent>
        </Card>

        {/* Partner Programme Table */}
        <Card className="border-2 border-amber-300 bg-white shadow-lg" data-testid="admin-partners-section">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Handshake className="w-5 h-5 text-amber-600" />
                Partner Programme
              </CardTitle>
              <CardDescription>Planners, venues and brands who registered interest</CardDescription>
            </div>
            <Button
              onClick={() => handleExport('partner', 'partners')}
              variant="outline"
              size="sm"
              className="border-2 border-amber-300"
              data-testid="admin-export-partners-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Partners
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-amber-600 mb-2" />
                <p className="text-gray-600">Loading partners...</p>
              </div>
            ) : (
              <SignupTable
                rows={partners}
                emptyText="No partner registrations yet"
                rowTestId="admin-partner-row"
                emailTestId="admin-partner-email"
                iconColor="text-amber-600"
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
